<!--
 * @Description: 设备缺陷列表模态框
 * @Author: System
 * @Date: 2024-01-01
-->
<template>
  <a-modal
    :title="title"
    :visible="visible"
    :width="1200"
    @cancel="handleCancel"
    :footer="null"
    :destroyOnClose="true"
  >
    <div>
      <!-- 设备信息展示 -->
      <div class="device-info" style="margin-bottom: 16px; padding: 12px; background: #f5f5f5; border-radius: 4px;">
        <h4 style="margin: 0 0 8px 0;">设备信息</h4>
        <p style="margin: 0;">设备名称：{{ deviceInfo.devName || '未知设备' }}</p>
      </div>

      <!-- 缺陷列表表格 -->
      <a-table
        :columns="columns"
        :dataSource="dataSource"
        :loading="loading"
        :pagination="pagination"
        @change="handleTableChange"
        bordered
        size="middle"
        rowKey="id"
      >
        <!-- 状态列自定义渲染 -->
        <template slot="statusSlot" slot-scope="text, record">
          <span v-if="record.comStatus == '1'">
            <a-tag color="#52c41a">已处理</a-tag>
          </span>
          <span v-else>
            <a-tag color="#faad14">未处理</a-tag>
          </span>
        </template>

        <!-- 类型列自定义渲染 -->
        <template slot="typeSlot" slot-scope="text">
          <span v-if="text === '投诉'">投诉</span>
          <span v-else-if="text === 'repair'">报修</span>
          <span v-else>其他</span>
        </template>

        <!-- 图片列自定义渲染 -->
        <template slot="imgSlot" slot-scope="text">
          <span style="font-size: 12px;font-style: italic;" v-if="!text">无图片</span>
          <img
            :src="getImgView(text)"
            alt="缺陷图片"
            height="25px"
            style="max-width:80px;font-size: 12px;font-style: italic; cursor: pointer;"
            v-else
            @click="previewImage(text)"
          />
        </template>

        <!-- 操作列 -->
        <template slot="actionSlot" slot-scope="text, record">
          <a @click="viewDetail(record)">详情</a>
        </template>
      </a-table>
    </div>
  </a-modal>
</template>

<script>
import { getAction } from '@/api/manage'
import { JeecgListMixin } from '@/mixins/JeecgListMixin'

export default {
  name: 'WfDevDefectModal',
  mixins: [JeecgListMixin],
  data() {
    return {
      title: '设备缺陷列表',
      visible: false,
      loading: false,
      deviceInfo: {}, // 设备信息
      dataSource: [], // 缺陷数据
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条记录`
      },
      // 表格列配置
      columns: [
        {
          title: '#',
          dataIndex: '',
          key: 'rowIndex',
          width: 60,
          align: 'center',
          customRender: (text, record, index) => {
            return (this.pagination.current - 1) * this.pagination.pageSize + index + 1
          }
        },
        {
          title: '反馈用户',
          align: 'center',
          dataIndex: 'wfUserId_dictText',
          width: 120
        },
        {
          title: '时间',
          align: 'center',
          dataIndex: 'createTime',
          width: 150
        },
        {
          title: '类型',
          align: 'center',
          dataIndex: 'type',
          width: 80,
          scopedSlots: { customRender: 'typeSlot' }
        },
        {
          title: '描述',
          align: 'center',
          dataIndex: 'description',
          ellipsis: true
        },
        {
          title: '图片',
          align: 'center',
          dataIndex: 'images',
          width: 100,
          scopedSlots: { customRender: 'imgSlot' }
        },
        {
          title: '状态',
          align: 'center',
          dataIndex: 'comStatus',
          width: 80,
          scopedSlots: { customRender: 'statusSlot' }
        },
        {
          title: '操作',
          dataIndex: 'action',
          align: 'center',
          width: 80,
          scopedSlots: { customRender: 'actionSlot' }
        }
      ]
    }
  },
  methods: {
    /**
     * 显示设备缺陷列表模态框
     * @param {Object} deviceRecord - 设备记录对象
     */
    show(deviceRecord) {
      this.visible = true
      this.deviceInfo = {
        devId: deviceRecord.devId,
        devName: deviceRecord.devId_dictText || '未知设备'
      }
      this.title = `设备缺陷列表 - ${this.deviceInfo.devName}`
      this.loadDefectList()
    },

    /**
     * 加载设备缺陷列表数据
     */
    async loadDefectList() {
      if (!this.deviceInfo.devId) {
        this.$message.warning('设备ID不能为空')
        return
      }

      this.loading = true
      try {
        const params = {
          devId: this.deviceInfo.devId,
          comStatus: '0', // 只查询未解决的缺陷
          pageNo: this.pagination.current,
          pageSize: this.pagination.pageSize
        }

        const response = await getAction('/wf/complaint/list', params)
        if (response.success) {
          this.dataSource = response.result.records || []
          this.pagination.total = response.result.total || 0
        } else {
          this.$message.error('加载缺陷列表失败：' + (response.message || '未知错误'))
          this.dataSource = []
          this.pagination.total = 0
        }
      } catch (error) {
        console.error('加载缺陷列表出错：', error)
        this.$message.error('加载缺陷列表失败，请稍后重试')
        this.dataSource = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },

    /**
     * 处理表格分页、排序、筛选变化
     * @param {Object} pagination - 分页信息
     * @param {Object} filters - 筛选信息
     * @param {Object} sorter - 排序信息
     */
    handleTableChange(pagination, filters, sorter) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadDefectList()
    },

    /**
     * 关闭模态框
     */
    handleCancel() {
      this.visible = false
      this.dataSource = []
      this.deviceInfo = {}
      this.pagination.current = 1
      this.pagination.total = 0
    },

    /**
     * 查看缺陷详情
     * @param {Object} record - 缺陷记录
     */
    viewDetail(record) {
      // 触发父组件的详情查看事件
      this.$emit('viewDetail', record)
    },

    /**
     * 预览图片
     * @param {String} imageUrl - 图片URL
     */
    previewImage(imageUrl) {
      if (imageUrl) {
        // 可以使用图片预览组件或者新窗口打开
        window.open(this.getImgView(imageUrl), '_blank')
      }
    },

    /**
     * 获取图片预览地址
     * @param {String} text - 图片路径
     * @returns {String} 完整的图片URL
     */
    getImgView(text) {
      if (!text) return ''
      if (text.indexOf(',') > 0) {
        text = text.substring(0, text.indexOf(','))
      }
      return window._CONFIG['domianURL'] + '/sys/common/view/' + text
    }
  }
}
</script>

<style scoped>
.device-info h4 {
  color: #1890ff;
  font-weight: 600;
}

.device-info p {
  color: #666;
  font-size: 14px;
}

/* 表格样式优化 */
.ant-table-tbody > tr > td {
  padding: 8px 16px;
}

/* 图片悬停效果 */
img:hover {
  opacity: 0.8;
  transform: scale(1.05);
  transition: all 0.3s ease;
}
</style>